import * as RadioGroup from '@radix-ui/react-radio-group';
import { combineRefs } from '@visiba/react-std';
import { ForwardedRef, forwardRef, ReactNode, useId } from 'react';
import { Controller, RegisterOptions } from 'react-hook-form';
import styled from 'styled-components';
import { useText } from '../../../utils/hooks/use_text';
import { Description } from '../base/description.component';
import { HelpText } from '../base/help_text.component';
import { Label } from '../base/label.component';
import { theme as commonTheme } from '../common_input.theme';
import { useFormContext } from '../form';
import { InputPropsHelper } from '../form/form.helpers';
import { InputRadioContext } from './_input_radio.context';
import { theme } from './input_radio.theme';

const elements = {
	group: styled.fieldset`
		--item-gap: ${theme.getValue('itemsGap')};
		--item-direction: column;
		--items-margin: ${theme.getValue('itemsMargin')};

		all: unset;

		user-select: none;
		-webkit-tap-highlight-color: transparent;
		-webkit-user-drag: none;
		-webkit-touch-callout: none;

		display: flex;
		flex-direction: column;

		&[data-direction='horizontal'] {
			--item-direction: row;
			--item-gap: ${theme.getValue('itemsGap')};

			&[data-variant='none'] {
				--item-gap: ${theme.getValue('itemsHorizontalGap')};
			}
		}

		&[data-variant='outlined'] {
			--item-gap: ${theme.getValue('outlinedItemsGap')};
			--items-margin: 0;
		}
	`,
	legend: styled(Label)`
		margin-bottom: ${commonTheme.getValue('labelMarginBottom')};
	`,
	description: styled(Description)`
		margin-bottom: ${commonTheme.getValue('descriptionMarginBottom')};
	`,
	items: styled.div`
		display: flex;
		flex-direction: var(--item-direction);
		gap: var(--item-gap);
		flex-wrap: wrap;
		margin: var(--items-margin) 0;
	`,
};

export type Direction = 'vertical' | 'horizontal';
type InputRadioVariants = 'none' | 'outlined';

export interface InputRadioRootProps {
	label: string;
	hideLabel?: boolean;
	description?: string;
	name?: string;
	direction?: Direction;
	helperText?: string;
	variant?: InputRadioVariants;
	onChange?: (value: string) => void;
	value?: string;
	defaultValue?: string;
	options?: RegisterOptions;
	required?: boolean;
	invalid?: boolean;
	invalidText?: string;
	readOnly?: boolean;
	disabled?: boolean;
	optional?: boolean;
	children: ReactNode;
}

export function CellulaInputRadioRoot(props: InputRadioRootProps, ref: ForwardedRef<HTMLFieldSetElement>): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const formContext = useFormContext();
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const staticTexts = useText();

	if (formContext == null || props.name == null) {
		return <InputRadioRoot {...props} />;
	}

	return (
		<Controller
			control={formContext.control.control}
			name={props.name}
			defaultValue={props.defaultValue}
			rules={{
				...props.options,
				onChange: props.onChange,
			}}
			disabled={props.disabled}
			render={({ field, fieldState, formState }) => {
				const inputPropsHelper = new InputPropsHelper(props, staticTexts, fieldState, formState);

				return (
					<InputRadioRoot
						{...props}
						{...field}
						optional={inputPropsHelper.isOptional()}
						required={inputPropsHelper.isRequired()}
						invalid={inputPropsHelper.isInvalid()}
						invalidText={inputPropsHelper.invalidText()}
						readOnly={inputPropsHelper.isReadOnly()}
						ref={combineRefs([ref, field.ref])}
					/>
				);
			}}
		/>
	);
}

const InputRadioRoot = forwardRef(function InputRadioRoot(
	{
		label,
		hideLabel,
		description,
		direction = 'vertical',
		helperText,
		name,
		variant = 'none',
		children,
		onChange,
		value,
		optional,
		invalid = false,
		invalidText,
		readOnly = false,
		disabled = false,
		required,
	}: InputRadioRootProps,
	ref: ForwardedRef<HTMLFieldSetElement>,
): JSX.Element {
	const id = useId();
	const descriptionId = `${id}-description`;
	const helpTextId = `${id}-help-text`;

	function handleValueChange(value: string): void {
		if (readOnly) {
			return;
		}

		onChange?.(value);
	}

	return (
		<InputRadioContext.Provider value={{ variant, invalid, readOnly, disabled }}>
			<RadioGroup.Root
				name={name}
				onValueChange={handleValueChange}
				value={value ?? (readOnly ? 'UNDEFINED_FAKE_VALUE' : undefined)}
				disabled={disabled}
				required={required}
				asChild
			>
				<elements.group
					data-variant={variant}
					data-direction={direction}
					aria-describedby={[descriptionId, helpTextId].join(' ')}
					aria-readonly={readOnly ?? undefined}
					aria-label={hideLabel ? label : undefined}
					ref={ref}
				>
					{label && !hideLabel ? (
						<elements.legend type='legend' optional={required ? false : optional}>
							{label}
						</elements.legend>
					) : null}
					<elements.description id={descriptionId}>{description}</elements.description>

					<elements.items>{children}</elements.items>

					<HelpText id={helpTextId} invalid={invalid}>
						{invalid && invalidText ? invalidText : helperText}
					</HelpText>
				</elements.group>
			</RadioGroup.Root>
		</InputRadioContext.Provider>
	);
});
